"use client";

import { Earth, Radio, Sparkles, Transgender, Users, Venus, Mars, VenusAndMars, X } from "lucide-react";
import Link from "@/components/ui/Link";
import { useSidebar } from "@/components/shell/sidebar-context";
import { cn } from "@/lib/cn";
import { compact } from "@/lib/format";
import { hasFilters, type StreamQuery, streamsHref } from "@/lib/stream-filters";
import type { StreamGender, StreamRegion } from "@/lib/stream-platforms";
import { PlatformLogo } from "./PlatformLogo";
import { platformChoices } from "./PlatformTabs";
import { type Facets, filterChoices, Pending } from "./StreamFilters";
import { useUi } from "@/components/ui/use-ui";

const GENDER_ICONS: Record<StreamGender, typeof Venus> = { female: Venus, male: Mars, couple: VenusAndMars, trans: Transgender };
const REGION_CODES: Record<StreamRegion, string> = { north_america: "NA", south_america: "SA", europe: "EU", asia: "AS", other: "··" };

/** A two-letter region mark, the size of the menu's icons. */
const RegionMark = ({ code }: { code: string }) => (
  <span className="grid size-[22px] shrink-0 place-items-center rounded-full text-[9px] font-medium tracking-wide ring-1 ring-current">{code}</span>
);

/** One choice, laid out like the menu's links: the icon stays in the rail, the label and count show when expanded. */
function Row({ href, active, count, icon, children }: { href: string; active: boolean; count?: number; icon: React.ReactNode; children: React.ReactNode }) {
  const { expanded, onNavigate } = useSidebar();
  return (
    <Link
      href={href}
      scroll={false}
      title={typeof children === "string" ? children : undefined}
      aria-current={active ? "true" : undefined}
      onClick={onNavigate}
      className={cn(
        "relative flex h-10 shrink-0 items-center gap-4 rounded-xl px-[13px] text-[14.5px] transition-colors",
        active ? "bg-white text-black" : "text-soft hover:bg-white/[0.06] hover:text-fg",
      )}
    >
      {icon}
      <span className={cn("flex min-w-0 flex-1 items-center gap-2 transition-opacity duration-200", expanded ? "opacity-100" : "md:opacity-0")}>
        <span className="min-w-0 flex-1 truncate whitespace-nowrap">{children}</span>
        <Pending />
        {count !== undefined && <span className={cn("tabular-nums text-[12px]", active ? "text-black/55" : "text-muted")}>{compact(count)}</span>}
      </span>
    </Link>
  );
}

/** A section title when expanded; a thin divider in the rail. */
function Heading({ children }: { children: React.ReactNode }) {
  const { expanded } = useSidebar();
  return (
    <div className="relative h-8 shrink-0">
      <span aria-hidden className={cn("absolute inset-x-3 top-1/2 border-t border-line transition-opacity duration-200", expanded ? "opacity-0" : "opacity-100")} />
      <p className={cn("absolute bottom-1.5 left-[13px] text-[11px] uppercase tracking-[0.08em] text-muted transition-opacity duration-200", expanded ? "opacity-100" : "md:opacity-0")}>
        {children}
      </p>
    </div>
  );
}

/**
 * The live streams' platforms and filters as the left menu shows them on the home page (from its @sidebar
 * slot): platforms, who is on camera, region, new models and the most used tags, each with its count.
 */
export function StreamMenu({ query, counts, facets }: { query: StreamQuery; counts: Record<string, number>; facets: Facets }) {
  const { expanded, onNavigate } = useSidebar();
  const t = useUi();
  const removeTag = t.cams.removeTag;
  const { genders, allGenders, regions, hasNew, tags } = filterChoices(query, facets);
  return (
    <nav aria-label={t.cams.platformsAndFilters} className="flex flex-col gap-0.5 px-2">
      <Heading>{t.cams.platforms}</Heading>
      {platformChoices(counts).map((p) => {
        const on = p.id === (query.platform?.id ?? null);
        return (
          <Row
            key={p.id ?? "all"}
            href={streamsHref({ ...query, platform: p.platform, tag: undefined })}
            active={on}
            count={p.live}
            icon={
              p.logo ? (
                <PlatformLogo logo={p.logo} size={22} />
              ) : (
                <span className={cn("grid size-[22px] shrink-0 place-items-center rounded-full", on ? "bg-brand-strong text-white" : "bg-brand/15 text-brand-soft")}>
                  <Radio size={13} />
                </span>
              )
            }
          >
            {p.id ? p.name : t.cams.allPlatforms}
          </Row>
        );
      })}

      {hasFilters(query) && (
        <Row href={streamsHref({ platform: query.platform })} active={false} icon={<X size={22} strokeWidth={1.6} className="shrink-0" />}>
          {t.cams.clearFilters}
        </Row>
      )}

      {genders.length > 0 && (
        <>
          <Heading>{t.cams.categories}</Heading>
          <Row href={streamsHref({ ...query, gender: undefined })} active={!query.gender} count={allGenders} icon={<Users size={22} strokeWidth={1.6} className="shrink-0" />}>
            {t.cams.all}
          </Row>
          {genders.map((g) => {
            const Icon = GENDER_ICONS[g.id];
            return (
              <Row
                key={g.id}
                href={streamsHref({ ...query, gender: g.id })}
                active={query.gender === g.id}
                count={facets.genders[g.id] ?? 0}
                icon={<Icon size={22} strokeWidth={1.6} className="shrink-0" />}
              >
                {t.cams.genders[g.id]}
              </Row>
            );
          })}
        </>
      )}

      {regions.length > 0 && (
        <>
          <Heading>{t.cams.region}</Heading>
          <Row href={streamsHref({ ...query, region: undefined })} active={!query.region} icon={<Earth size={22} strokeWidth={1.6} className="shrink-0" />}>
            {t.cams.allRegions}
          </Row>
          {regions.map((r) => (
            <Row
              key={r.id}
              href={streamsHref({ ...query, region: r.id })}
              active={query.region === r.id}
              count={facets.regions[r.id] ?? 0}
              icon={<RegionMark code={REGION_CODES[r.id]} />}
            >
              {t.cams.regions[r.id]}
            </Row>
          ))}
        </>
      )}

      {hasNew && (
        <>
          <Heading>{t.cams.more}</Heading>
          <Row
            href={streamsHref({ ...query, isNew: query.isNew ? undefined : true })}
            active={!!query.isNew}
            count={facets.newCount}
            icon={<Sparkles size={22} strokeWidth={1.6} className="shrink-0" />}
          >
            {t.cams.newModels}
          </Row>
        </>
      )}

      {tags.length > 0 && (
        <>
          <Heading>{t.cams.tags}</Heading>
          {/* Too many for the rail: they show with the labels. */}
          <div className={cn("flex flex-wrap gap-1.5 px-1 pb-3 transition-opacity duration-200", expanded ? "opacity-100" : "md:pointer-events-none md:opacity-0")}>
            {tags.map((t) => {
              const on = t.tag === query.tag;
              return (
                <Link
                  key={t.tag}
                  href={streamsHref({ ...query, tag: on ? undefined : t.tag })}
                  scroll={false}
                  onClick={onNavigate}
                  aria-current={on ? "true" : undefined}
                  className={cn(
                    "flex h-7 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[12.5px] transition-colors",
                    on ? "bg-white text-black" : "chip text-soft hover:text-fg",
                  )}
                >
                  #{t.tag}
                  {on ? <X size={12} aria-label={removeTag} /> : <span className="tabular-nums text-[11px] text-muted">{compact(t.count)}</span>}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </nav>
  );
}

/** While the counts load. */
export function StreamMenuSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 px-2 pt-8" aria-hidden>
      {Array.from({ length: 12 }, (_, i) => (
        <span key={i} className="skeleton block h-9 rounded-xl" />
      ))}
    </div>
  );
}
